import React from 'react';
import { supabase } from '../../lib/supabase';
import { conferirDetalhe, proximaPendente, totaisDaLinha } from '../../utils/contabil/conferencia';
import {
  TEXTO, SUAVE, BORDA, VERDE, AMBAR, VERMELHO, ROXO, fmtMoeda, fmtData, fmtDataHora,
  botaoPrimario, botaoSecundario, botaoPequeno, th, td, desabilitado, SeloSituacao,
} from './comum';

// Etapa 3 — Conferir as contas do balancete do mês, uma a uma, em painel lateral.
// Lista à esquerda; à direita o detalhe com saldo anterior, cada lançamento,
// saldo final, observação e os botões Validar / Desfazer / Próxima conta.

const COR_FLAG = { erro: VERMELHO, alerta: AMBAR, info: '#1d4ed8' };

// Lançamento direto na conta que está sendo conferida (ajuste, provisão,
// reclassificação). Grava em contabil_lancamentos; os gatilhos do banco recusam
// mês fechado e data antes do corte, e a lista recarrega pelo tempo real + onLancado.
function NovoLancamentoConta({ conta, contas, empresaSlug, inicioMes, fimMes, onLancado, setAviso, inputStyle, labelStyle }) {
  const vazio = { lado: 'D', contrapartida: '', data: fimMes, historico: '', valor: '', documento: '' };
  const [aberto, setAberto] = React.useState(false);
  const [f, setF] = React.useState(vazio);
  const [salvando, setSalvando] = React.useState(false);
  const set = (campo) => (e) => setF(p => ({ ...p, [campo]: e.target.value }));

  const salvar = async () => {
    const valor = Math.round(parseFloat(String(f.valor).replace(',', '.')) * 100) / 100;
    const faltando = [!f.contrapartida && 'Contrapartida', !f.data && 'Data', !f.historico.trim() && 'Histórico', !(valor > 0) && 'Valor'].filter(Boolean);
    if (faltando.length) { setAviso({ tipo: 'erro', texto: `Preencha: ${faltando.join(', ')}.` }); return; }
    if (f.data < inicioMes || f.data > fimMes) {
      setAviso({ tipo: 'erro', texto: `A data precisa estar entre ${fmtData(inicioMes)} e ${fmtData(fimMes)}.` });
      return;
    }
    if (f.contrapartida === conta.rotulo) { setAviso({ tipo: 'erro', texto: 'A contrapartida não pode ser a própria conta.' }); return; }
    setSalvando(true);
    const { error } = await supabase.from('contabil_lancamentos').insert({
      empresa_slug: empresaSlug,
      data: f.data,
      historico: f.historico.trim(),
      conta_debito: f.lado === 'D' ? conta.rotulo : f.contrapartida,
      conta_credito: f.lado === 'D' ? f.contrapartida : conta.rotulo,
      valor,
      documento: f.documento.trim() || null,
      origem: 'manual',
    });
    setSalvando(false);
    if (error) {
      const m = error.message || '';
      if (!/está fechado|anterior ao início da escrituração/.test(m)) console.error('[conferencia] Falha ao registrar lançamento.', error);
      setAviso({ tipo: 'erro', texto: /está fechado|anterior ao início da escrituração/.test(m) ? m : 'Não foi possível registrar o lançamento. Tente novamente.' });
      return;
    }
    setAviso({ tipo: 'sucesso', texto: `Lançamento de ${fmtMoeda(valor)} registrado em ${conta.rotulo}.` });
    setF(vazio);
    setAberto(false);
    if (onLancado) onLancado();
  };

  if (!aberto) {
    return (
      <button onClick={() => setAberto(true)} style={{ ...botaoSecundario, marginTop: 10 }}>+ Novo lançamento nesta conta</button>
    );
  }

  const campo = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 };
  return (
    <div style={{ marginTop: 10, border: `1px solid ${ROXO}`, borderRadius: 8, padding: 12, background: 'rgba(124,58,237,0.03)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: ROXO, marginBottom: 10 }}>Novo lançamento em {conta.rotulo}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <div style={campo}>
          <label htmlFor="nlc-lado" style={labelStyle}>Esta conta será</label>
          <select id="nlc-lado" style={inputStyle} value={f.lado} onChange={set('lado')}>
            <option value="D">Debitada (D)</option>
            <option value="C">Creditada (C)</option>
          </select>
        </div>
        <div style={{ ...campo, gridColumn: 'span 2' }}>
          <label htmlFor="nlc-contra" style={labelStyle}>Contrapartida ({f.lado === 'D' ? 'crédito' : 'débito'})</label>
          <select id="nlc-contra" style={inputStyle} value={f.contrapartida} onChange={set('contrapartida')}>
            <option value="">— Selecione a conta —</option>
            {contas.filter(c => c.codigo !== conta.codigo).map(c => <option key={c.codigo} value={c.rotulo}>{c.rotulo}</option>)}
          </select>
        </div>
        <div style={campo}>
          <label htmlFor="nlc-data" style={labelStyle}>Data</label>
          <input id="nlc-data" type="date" min={inicioMes} max={fimMes} style={inputStyle} value={f.data} onChange={set('data')} />
        </div>
        <div style={{ ...campo, gridColumn: 'span 2' }}>
          <label htmlFor="nlc-hist" style={labelStyle}>Histórico</label>
          <input id="nlc-hist" style={inputStyle} placeholder="Ex.: Ajuste de saldo conforme NF 123" value={f.historico} onChange={set('historico')} />
        </div>
        <div style={campo}>
          <label htmlFor="nlc-valor" style={labelStyle}>Valor (R$)</label>
          <input id="nlc-valor" type="number" step="0.01" min="0" style={inputStyle} value={f.valor} onChange={set('valor')} />
        </div>
        <div style={campo}>
          <label htmlFor="nlc-doc" style={labelStyle}>Documento</label>
          <input id="nlc-doc" style={inputStyle} placeholder="NF, recibo..." value={f.documento} onChange={set('documento')} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button onClick={() => { setAberto(false); setF(vazio); }} style={botaoSecundario}>Cancelar</button>
        <button onClick={salvar} disabled={salvando} style={desabilitado(botaoPrimario, salvando)}>{salvando ? 'Registrando…' : 'Registrar lançamento'}</button>
      </div>
    </div>
  );
}

export default function EtapaConferir({
  itens, totalDebitos, totalCreditos, aClassificar, mesFechado, nomeMes,
  fechamento, codigosAntigos, onIrEtapa, setAviso, cardStyle, inputStyle, labelStyle,
  empresaSlug, contas = [], inicioMes, fimMes, onLancado,
}) {
  const [selecionada, setSelecionada] = React.useState(null);
  const [soPendentes, setSoPendentes] = React.useState(false);
  const [observacao, setObservacao] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [confirmarAntigas, setConfirmarAntigas] = React.useState(false);

  const visiveis = soPendentes ? itens.filter(i => i.situacao !== 'validada') : itens;

  // Seleção inicial (e quando a conta some do balancete): primeira não validada.
  React.useEffect(() => {
    if (selecionada && itens.some(i => i.codigo === selecionada)) return;
    const primeira = itens.find(i => i.situacao !== 'validada') || itens[0];
    setSelecionada(primeira ? primeira.codigo : null);
  }, [itens, selecionada]);

  const atual = itens.find(i => i.codigo === selecionada) || null;

  React.useEffect(() => {
    setObservacao((atual && atual.validacao && atual.validacao.observacao) || '');
    // Só ao trocar de conta: não sobrescreve o que está sendo digitado a cada recarga.
  }, [selecionada]); // eslint-disable-line react-hooks/exhaustive-deps

  const conferencia = atual ? conferirDetalhe(atual.linha, atual.lancs) : null;
  const proxima = atual ? proximaPendente(itens, atual.codigo) : null;

  const acao = async (tipo) => {
    if (!atual || salvando) return;
    setSalvando(true);
    setAviso(null);
    const r = tipo === 'validar'
      ? await fechamento.validar(atual.codigo, { observacao, totais: totaisDaLinha(atual.linha) })
      : await fechamento.desfazer(atual.codigo, { observacao });
    setSalvando(false);
    if (!r.ok) {
      setAviso({ tipo: 'erro', texto: tipo === 'validar' ? 'Não foi possível validar a conta. Tente novamente.' : 'Não foi possível desfazer a validação. Tente novamente.' });
    }
  };

  const trazerAntigas = async () => {
    const porCodigo = new Map(itens.map(i => [i.codigo, i]));
    const lista = codigosAntigos.filter(c => porCodigo.has(c)).map(c => ({ conta: c, totais: totaisDaLinha(porCodigo.get(c).linha) }));
    if (!lista.length) return;
    setSalvando(true);
    const r = await fechamento.importarAntigas(lista);
    setSalvando(false);
    setConfirmarAntigas(false);
    setAviso(r.ok
      ? { tipo: 'sucesso', texto: `${lista.length} validação(ões) trazida(s) do Balancete. Confira as contas com alerta antes de fechar.` }
      : { tipo: 'erro', texto: 'Não foi possível trazer as validações anteriores. Tente novamente.' });
  };

  const validadas = itens.filter(i => i.situacao === 'validada').length;
  const antigasAplicaveis = codigosAntigos.filter(c => itens.some(i => i.codigo === c));
  const mostrarAntigas = !fechamento.carregando && fechamento.validacoes.length === 0 && antigasAplicaveis.length > 0 && !mesFechado;
  const diferenca = Math.round((totalDebitos - totalCreditos) * 100) / 100;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h4 style={{ color: TEXTO, margin: 0 }}>Conferência das contas — {nomeMes}</h4>
        <span style={{ fontSize: 12, color: SUAVE }}>
          {validadas} de {itens.length} validada(s) · Débitos {fmtMoeda(totalDebitos)} · Créditos {fmtMoeda(totalCreditos)}
          {Math.abs(diferenca) > 0 && <strong style={{ color: VERMELHO }}> · Diferença {fmtMoeda(diferenca)}</strong>}
        </span>
      </div>
      <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
        Todas as contas com saldo ou movimento no mês. Confira os lançamentos de cada uma e valide.
      </p>

      {aClassificar > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: AMBAR, marginBottom: 12 }}>
          Há {aClassificar} lançamento(s) em A Classificar no mês; eles mudam os saldos das contas.
          <button onClick={() => onIrEtapa(2)} style={botaoPequeno}>Classificar</button>
        </div>
      )}

      {mostrarAntigas && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.35)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#1d4ed8' }}>
          <span>{antigasAplicaveis.length} conta(s) deste mês foram validadas na tela do Balancete antes do assistente.</span>
          {confirmarAntigas ? (
            <>
              <button onClick={trazerAntigas} disabled={salvando} style={desabilitado({ ...botaoPequeno, color: ROXO, border: `1px solid ${ROXO}` }, salvando)}>Confirmar</button>
              <button onClick={() => setConfirmarAntigas(false)} style={botaoPequeno}>Cancelar</button>
            </>
          ) : (
            <button onClick={() => setConfirmarAntigas(true)} style={botaoPequeno}>Trazer validações anteriores</button>
          )}
        </div>
      )}

      {itens.length === 0 ? (
        <div style={{ fontSize: 13, color: SUAVE }}>Nenhuma conta com saldo ou movimento neste mês.</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Lista de contas */}
          <div style={{ flex: '1 1 240px', maxWidth: 300, minWidth: 0 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: TEXTO, marginBottom: 8 }}>
              <input type="checkbox" checked={soPendentes} onChange={e => setSoPendentes(e.target.checked)} />
              Só pendentes
            </label>
            <div role="listbox" aria-label="Contas do balancete" style={{ maxHeight: 560, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8 }}>
              {visiveis.length === 0 && <div style={{ padding: 12, fontSize: 12, color: VERDE }}>Todas as contas estão validadas.</div>}
              {visiveis.map(i => {
                const ativa = i.codigo === selecionada;
                return (
                  <button
                    key={i.codigo}
                    role="option"
                    aria-selected={ativa}
                    onClick={() => setSelecionada(i.codigo)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', border: 'none',
                      borderBottom: '1px solid #f1eef7', borderLeft: `3px solid ${ativa ? ROXO : 'transparent'}`,
                      background: ativa ? 'rgba(124,58,237,0.08)' : '#ffffff', padding: '8px 10px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontFamily: 'monospace', color: SUAVE, fontWeight: 600 }}>{i.codigo}</span>
                      <SeloSituacao situacao={i.situacao} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 13, color: TEXTO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.linha.nome}</span>
                      <span style={{ fontSize: 12, fontFamily: 'monospace', color: TEXTO, whiteSpace: 'nowrap' }}>{fmtMoeda(i.linha.saldoFinal)}</span>
                    </div>
                    {i.flag && (
                      <div style={{ fontSize: 11, color: COR_FLAG[i.flag.tipo] || SUAVE, marginTop: 2 }}>{i.flag.msg}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detalhe da conta */}
          {atual && (
            <div style={{ flex: '3 1 480px', minWidth: 0, border: `1px solid ${BORDA}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <h5 style={{ margin: 0, fontSize: 15, color: TEXTO }}>{atual.codigo} — {atual.linha.nome}</h5>
                <SeloSituacao situacao={atual.situacao} />
              </div>
              {atual.validacao && atual.validacao.status === 'validado' && (
                <div style={{ fontSize: 11, color: SUAVE, marginTop: 4 }}>
                  Validada em {fmtDataHora(atual.validacao.validado_em)}
                  {atual.situacao === 'desatualizada' && <span style={{ color: VERMELHO }}> · Alterada após a validação: confira e valide novamente.</span>}
                </div>
              )}
              {atual.flag && (
                <div style={{ fontSize: 12, color: COR_FLAG[atual.flag.tipo] || SUAVE, marginTop: 6 }}>{atual.flag.msg}</div>
              )}

              <div style={{ maxHeight: 520, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8, marginTop: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 84 }}>Data</th>
                      <th style={th}>Histórico e contrapartida</th>
                      <th style={{ ...th, textAlign: 'right', width: 112 }}>Débito</th>
                      <th style={{ ...th, textAlign: 'right', width: 112 }}>Crédito</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: '#f7f5fb' }}>
                      <td style={td} colSpan={2}><strong>Saldo anterior</strong></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }} colSpan={2}>{fmtMoeda(atual.linha.saldoAnterior)}</td>
                    </tr>
                    {atual.lancs.length === 0 && (
                      <tr><td style={{ ...td, color: SUAVE }} colSpan={4}>Sem movimento no mês; só saldo anterior.</td></tr>
                    )}
                    {atual.lancs.map((lc, k) => (
                      <tr key={`${lc.dbId || lc.lancIdx}-${lc.lado}-${k}`}>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 13 }}>{fmtData(lc.data)}</td>
                        <td style={{ ...td, fontSize: 13, lineHeight: 1.4 }}>
                          <div style={{ wordBreak: 'break-word' }}>{lc.historico}</div>
                          <div style={{ fontSize: 12, color: ROXO, marginTop: 2 }}>
                            ↔ {lc.contrapartida || '—'}
                            {(lc.fonte || lc.origem) && <span style={{ color: SUAVE }}> · {lc.fonte || lc.origem}</span>}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'nowrap' }}>{lc.lado === 'D' ? fmtMoeda(lc.valor) : ''}</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'nowrap' }}>{lc.lado === 'C' ? fmtMoeda(lc.valor) : ''}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f7f5fb' }}>
                      <td style={td} colSpan={2}><strong>Movimento do mês</strong></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoeda(atual.linha.debitos)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoeda(atual.linha.creditos)}</td>
                    </tr>
                    <tr style={{ background: '#f7f5fb' }}>
                      <td style={td} colSpan={2}><strong>Saldo final</strong></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }} colSpan={2}>{fmtMoeda(atual.linha.saldoFinal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!mesFechado && empresaSlug && (
                <NovoLancamentoConta
                  key={atual.codigo}
                  conta={contas.find(c => c.codigo === atual.codigo) || { codigo: atual.codigo, rotulo: `${atual.codigo} — ${atual.linha.nome}` }}
                  contas={contas}
                  empresaSlug={empresaSlug}
                  inicioMes={inicioMes}
                  fimMes={fimMes}
                  onLancado={onLancado}
                  setAviso={setAviso}
                  inputStyle={inputStyle}
                  labelStyle={labelStyle}
                />
              )}

              {conferencia && (
                <div style={{ fontSize: 12, marginTop: 8, color: conferencia.confere ? VERDE : VERMELHO }}>
                  {conferencia.confere
                    ? 'A soma dos lançamentos confere com a linha do balancete.'
                    : `A soma dos lançamentos não confere com a linha do balancete (débitos ${fmtMoeda(conferencia.debitos)}, créditos ${fmtMoeda(conferencia.creditos)}). Isso ocorre com o encerramento de exercício em dezembro; nos demais casos, acione o suporte.`}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
                <label htmlFor="fm-observacao" style={labelStyle}>Observação</label>
                <textarea
                  id="fm-observacao"
                  value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                  disabled={mesFechado}
                  rows={2}
                  placeholder="Opcional. Ex.: conferido com o extrato de 31/05."
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {mesFechado ? (
                  <span style={{ fontSize: 12, color: SUAVE }}>O mês está fechado; a validação não pode ser alterada.</span>
                ) : (
                  <>
                    <button onClick={() => acao('validar')} disabled={salvando} style={desabilitado(botaoPrimario, salvando)}>
                      {salvando ? 'Gravando…' : atual.situacao === 'desatualizada' ? 'Validar novamente' : atual.situacao === 'validada' ? 'Salvar observação' : 'Validar'}
                    </button>
                    {atual.validacao && atual.validacao.status === 'validado' && (
                      <button onClick={() => acao('desfazer')} disabled={salvando} style={desabilitado(botaoSecundario, salvando)}>Desfazer validação</button>
                    )}
                  </>
                )}
                <button onClick={() => proxima && setSelecionada(proxima)} disabled={!proxima} style={desabilitado(botaoSecundario, !proxima)}>
                  Próxima conta
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
